import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import {
  MatOptionModule,
  MatOptionSelectionChange,
} from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { SearchInputOptionTemplateDirective } from '../search-input/search-input-option-template.directive';
import { ButtonComponent } from 'src/app/components/button/button.component';
import { IconComponent } from 'src/app/components/icon/icon.component';
import { InputRegexDirective } from '../../directives/input-regex.directive';

export type AutocompleteConfig<T = string> = {
  list: T[];
  getObjectPropValue?: (object: T | string) => string;
  getObjectSearchValue?: (object: T | string) => string;
};

@Component({
    selector: 'app-suggestion-search-input',
    templateUrl: './suggestion-search-input.component.html',
    styleUrls: ['./suggestion-search-input.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatOptionModule,
        MatAutocompleteModule,
        MatInputModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        ButtonComponent,
        IconComponent,
        InputRegexDirective,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SuggestionSearchInputComponent<T = string> {
  @Input() id?: string; // Propriedade adicionada para automação

  @Input() autocompleteConfig: AutocompleteConfig<T> = {
    list: [],
    getObjectPropValue: (value: string) => '',
  };
  @Input() inputLabel = '';
  @Input() placeholder = '';
  @Input() disabled = false;
  @Output() searchInput = new EventEmitter<string>();

  @ContentChild(SearchInputOptionTemplateDirective, { read: TemplateRef })
  layoutOpcaoTemplate!: TemplateRef<any>;

  inputSearchValue = '';

  get optionSearchListFiltered() {
    if (!this.autocompleteConfig) return [];

    const {
      list,
      getObjectSearchValue: getValueSearch,
      getObjectPropValue: getValueProp,
    } = this.autocompleteConfig;

    const valueToCompare = getValueSearch || getValueProp;

    return list.filter((option) => {
      const listValue = (
        valueToCompare ? valueToCompare(option) : option.toString()
      ).toLowerCase();
      const searchedValue = this.inputSearchValue.toLowerCase();

      return listValue.includes(searchedValue);
    });
  }

  handleSearch() {
    if (this.disabled) return;

    this.searchInput.emit(this.inputSearchValue);
  }

  onOptionSelectionChange(event: MatOptionSelectionChange<T>, option: T) {
    if (event.source.selected) {
      this.handleSelectOption(option);
    }
  }

  handleSelectOption(option: T) {
    if (this.autocompleteConfig.getObjectPropValue) {
      this.inputSearchValue =
        this.autocompleteConfig.getObjectPropValue(option);
    } else {
      this.inputSearchValue = option.toString();
    }
  }

  handleClearSearch() {
    this.inputSearchValue = '';
    this.handleSearch();
  }

  onChangeSearch(value: string) {
    this.inputSearchValue = value;
  }

  selectOption(value: string) {
    this.inputSearchValue = value;
  }
}
